import React, { useEffect, useRef, useState } from 'react'
import { RxHamburgerMenu } from 'react-icons/rx'
import './style.css'
import Button from '../../components/atoms/Buttons'
import FormInput from '../../components/atoms/Input/Form-Input'
import { useUserContext } from '../../context/UserContext'
import EditEventModal from '../../components/atoms/Modal/EditEventModal/EditEventModal'
import CreateEventModal from '../../components/atoms/Modal/CreateEventModal/CreateEventModal'
import Sidebar from '../../components/Common/Sidebar/Sidebar'
import EventCard from './EventCard/EventCard'
import AdminProfile from '../../components/AdminProfile/AdminProfile'
import MobileSideBar from '../../components/MobileSideBar/MobileSideBar'
import { DELETE_EVENTS } from '../../apollo/mutations/event'
import { useMutation, useQuery } from '@apollo/client'
import MessageModal from '../../components/atoms/Modal/MessageModal/MessageModal'
import { LIST_EVENTS } from '../../apollo/queries/event'
import CustomDatePicker from '../../components/atoms/Input/CustomDatePicker'
import moment from 'moment'
import MainLayout from '../../components/Layout'
import Spinner from '../../components/Spinner/Spinner'

/** Event fully ended: endDate (or startDate) YYYY-MM-DD is strictly before today. */
function isPastEvent(ev, todayYmd) {
  const end = (ev.endDate || ev.startDate || '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) return false
  return end < todayYmd
}

function EventManagement() {
  const dropdownRef = useRef(null)
  const sidebarRef = useRef(null)

  const { modalState, closeMessageModal, editModal, cardId, userLoading } =
    useUserContext()

  const [showCreate, setShowCreate] = useState(false)
  const [deleteEvents, { loading: deleteLoading }] = useMutation(DELETE_EVENTS)
  // Wait for Cognito + user bootstrap so AppSync gets a JWT, not API key (API key uses a stricter listEvents filter and often returns [] for admins).
  const { loading, data: EventsData } = useQuery(LIST_EVENTS, {
    skip: userLoading,
    fetchPolicy: 'network-only',
  })

  const [isOpen, setIsOpen] = useState(false)
  const [toggle, setToggle] = useState(false)
  const [selectedEvents, setSelectedEvents] = useState([])

  const [search, setSearch] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [filterLocation, setFilterLocation] = useState('')

  const handleSelectEvent = eventId => {
    setSelectedEvents(prev =>
      prev.includes(eventId)
        ? prev.filter(id => id !== eventId)
        : [...prev, eventId],
    )
  }

  const filteredEvents =
    EventsData?.listEvents?.items?.filter(event => {
      const matchesSearch =
        search === '' ||
        Object.entries(event).some(([key, value]) => {
          if (
            [
              'date',
              'startDate',
              'endDate',
              'eventLocation',
              'country',
              'state',
              'city',
            ].includes(key)
          ) {
            return false
          }
          if (typeof value === 'object' && value !== null) {
            return Object.values(value).some(nestedValue =>
              String(nestedValue).toLowerCase().includes(search.toLowerCase()),
            )
          }
          return String(value).toLowerCase().includes(search.toLowerCase())
        })

      const matchesLocation =
        filterLocation === '' ||
        (event.state &&
          event.state.toLowerCase().includes(filterLocation.toLowerCase())) ||
        (event.city &&
          event.city.toLowerCase().includes(filterLocation.toLowerCase())) ||
        (event.country &&
          event.country.toLowerCase().includes(filterLocation.toLowerCase()))

      const matchesDate = (() => {
        try {
          if (!startDate && !endDate) return true

          const selectedStartDate = startDate
            ? moment(startDate, ['YYYY-MM-DD', 'MM-DD-YYYY'], true)
            : null
          const selectedEndDate = endDate
            ? moment(endDate, ['YYYY-MM-DD', 'MM-DD-YYYY'], true)
            : null
          // API may return ISO datetimes; strict date-only formats would fail and hide rows.
          const eventStart = moment(event.startDate)
          const eventEnd = event.endDate
            ? moment(event.endDate)
            : eventStart.clone()
          if (!eventStart.isValid()) return true

          if (selectedStartDate && selectedEndDate) {
            return (
              eventStart.isBetween(
                selectedStartDate,
                selectedEndDate,
                'day',
                '[]',
              ) ||
              eventEnd.isBetween(
                selectedStartDate,
                selectedEndDate,
                'day',
                '[]',
              ) ||
              selectedStartDate.isBetween(eventStart, eventEnd, 'day', '[]') ||
              selectedEndDate.isBetween(eventStart, eventEnd, 'day', '[]')
            )
          }

          if (selectedStartDate) {
            return (
              eventStart.isSameOrAfter(selectedStartDate) ||
              eventEnd.isSameOrAfter(selectedStartDate)
            )
          }

          if (selectedEndDate) {
            return (
              eventStart.isSameOrBefore(selectedEndDate) ||
              eventEnd.isSameOrBefore(selectedEndDate)
            )
          }

          return true
        } catch (err) {
          console.error('Date filter error:', err)
          return false
        }
      })()

      return matchesSearch && matchesLocation && matchesDate
    }) || []

  const todayYmd = moment().format('YYYY-MM-DD')
  const filteredCurrentEvents = filteredEvents.filter(
    ev => !isPastEvent(ev, todayYmd),
  )
  const filteredArchivedEvents = filteredEvents.filter(ev =>
    isPastEvent(ev, todayYmd),
  )

  const currentIds = filteredCurrentEvents.map(e => e.id)
  const archivedIds = filteredArchivedEvents.map(e => e.id)
  const allCurrentSelected =
    currentIds.length > 0 && currentIds.every(id => selectedEvents.includes(id))
  const allArchivedSelected =
    archivedIds.length > 0 &&
    archivedIds.every(id => selectedEvents.includes(id))

  const handleSelectAllCurrent = () => {
    if (allCurrentSelected) {
      setSelectedEvents(prev => prev.filter(id => !currentIds.includes(id)))
    } else {
      setSelectedEvents(prev => [...new Set([...prev, ...currentIds])])
    }
  }

  const handleSelectAllArchived = () => {
    if (allArchivedSelected) {
      setSelectedEvents(prev => prev.filter(id => !archivedIds.includes(id)))
    } else {
      setSelectedEvents(prev => [...new Set([...prev, ...archivedIds])])
    }
  }

  const handleBulkDelete = async () => {
    if (selectedEvents.length === 0) return

    try {
      await deleteEvents({
        variables: { ids: selectedEvents },
        refetchQueries: ['listEvents'],
        awaitRefetchQueries: true,
      })
      setSelectedEvents([])
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    function handleClickOutside(event) {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setToggle(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleNewEvent = () => setShowCreate(true)

  const dateInputClass =
    'font-montserrat text-[14px] w-full min-w-0 px-4 sm:px-5 h-[50px] rounded-[12px] border border-[#c3c9d4] box-border'

  return (
    <MainLayout>
      <div className="w-full space-y-10">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <h1 className="events !leading-none shrink-0">Events</h1>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={handleNewEvent} className="event-action-btn event-action-btn-fluid">
              Create Event
            </Button>
          </div>
        </div>

        <section className="event-filters rounded-2xl border border-[#e8eaef] bg-[#fafbfc] p-6 sm:p-8 flex flex-col gap-6">
          <h2 className="current-events !text-2xl sm:!text-[28px] !mb-0 shrink-0">
            Filter events
          </h2>
          <div className="grid w-full min-w-0 grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2 xl:grid-cols-4 xl:gap-x-8 items-start">
            <div className="min-w-0 w-full max-w-full">
              <FormInput
                name="search"
                label="Search"
                type="text"
                placeholder="Search events..."
                onChange={e => setSearch(e.target.value)}
                className="w-full min-w-0 max-w-full"
              />
            </div>
            <div className="min-w-0 w-full max-w-full">
              <label
                className="event-filter-field__label"
                htmlFor="event-filter-start-date"
              >
                Start Date
              </label>
              <CustomDatePicker
                clearable
                floatingLabel={false}
                inputId="event-filter-start-date"
                value={startDate}
                onChange={date => setStartDate(date || '')}
                label="Start Date"
                placeholder="Select Start Date"
                isValidDate={current =>
                  !endDate || current.isBefore(moment(endDate))
                }
                inputClassName={dateInputClass}
                className="w-full min-w-0 max-w-full"
              />
            </div>
            <div className="min-w-0 w-full max-w-full">
              <label
                className="event-filter-field__label"
                htmlFor="event-filter-end-date"
              >
                End Date
              </label>
              <CustomDatePicker
                clearable
                floatingLabel={false}
                inputId="event-filter-end-date"
                value={endDate}
                onChange={date => setEndDate(date || '')}
                label="End Date"
                placeholder="Select End Date"
                isValidDate={current =>
                  !startDate || current.isAfter(moment(startDate))
                }
                inputClassName={dateInputClass}
                className="w-full min-w-0 max-w-full"
              />
            </div>
            <div className="min-w-0 w-full max-w-full">
              <FormInput
                name="filterLocation"
                label="By Location"
                type="text"
                placeholder="City, state..."
                onChange={e => setFilterLocation(e.target.value)}
                className="w-full min-w-0 max-w-full"
              />
            </div>
          </div>
        </section>

      {showCreate && <CreateEventModal onClose={() => setShowCreate(false)} />}
      {editModal && <EditEventModal key={cardId} />}
      {modalState?.isOpen && (
        <MessageModal
          title={modalState?.title}
          description={modalState?.message}
          showModal={modalState?.isOpen}
          onClose={closeMessageModal}
        />
      )}

        {loading ? (
          <div className="flex justify-center items-center min-h-[240px] py-16">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="current-events !text-2xl sm:!text-[28px] !mb-0 shrink-0">
                Current events
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={handleSelectAllCurrent}
                  className="event-action-btn event-action-btn-fluid"
                  disabled={deleteLoading || currentIds.length === 0}
                >
                  {allCurrentSelected ? 'Deselect All' : 'Select All'}
                </Button>
                {selectedEvents.length > 0 && (
                  <Button
                    onClick={handleBulkDelete}
                    className="event-action-btn event-action-btn-fluid !bg-red-500"
                    disabled={deleteLoading}
                  >
                    {`Delete (${selectedEvents.length})`}
                  </Button>
                )}
              </div>
            </div>

            <EventCard
              loading={false}
              EventsData={filteredCurrentEvents}
              deleteLoading={deleteLoading}
              selectedEvents={selectedEvents}
              onSelectEvent={handleSelectEvent}
              emptyMessage="No current events match your filters."
            />

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mt-14">
              <h2 className="current-events !text-2xl sm:!text-[28px] !mb-0 shrink-0">
                Archived events
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  onClick={handleSelectAllArchived}
                  className="event-action-btn event-action-btn-fluid"
                  disabled={deleteLoading || archivedIds.length === 0}
                >
                  {allArchivedSelected ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
            </div>

            <EventCard
              loading={false}
              EventsData={filteredArchivedEvents}
              deleteLoading={deleteLoading}
              selectedEvents={selectedEvents}
              onSelectEvent={handleSelectEvent}
              emptyMessage="No archived events match your filters."
            />
          </>
        )}
      </div>
    </MainLayout>
  )
}

export default EventManagement
